import { QueryFunctionContext } from '@tanstack/react-query'
import { HttpClient } from '@opallabs/api'
import { Color } from '@opallabs/gem'
import { iconForEntity } from '../GlobalSearch/helpers'
import { SearchResult, SearchResponse, SearchResource } from '../types'

type SearchQueryParams = {
  searchTerm?: string
  workspaceSlug?: string
  workspaceId?: string
}
type SearchQueryKey = [string, SearchQueryParams]

type ViewResult = Pick<
  SearchResult,
  'id' | 'icon' | 'display_name' | 'navigation_path' | 'useHypernovaIcon'
>

// The search endpoint is not JSON:API compliant, so it gets it's own special query function.
export const searchQueryFunction = async (
  args: QueryFunctionContext<SearchQueryKey>
): Promise<SearchResult[]> => {
  const [key, queryParams] = args.queryKey
  const { searchTerm, workspaceSlug, workspaceId } = queryParams

  if (searchTerm === undefined)
    throw new Error('Unable to search without a valid search term')
  if (workspaceSlug === undefined || workspaceId === undefined)
    throw new Error('Unable to search without a valid Workspace')

  const url = `${key}?query=${searchTerm}&slug=${workspaceSlug}&brand_id=${workspaceId}`

  try {
    const response = await HttpClient().get(url)
    return mapSearchResults(response?.data, workspaceSlug, searchTerm)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    throw new Error(
      `An API Error has occured: ${error?.message || 'Unknown Error'}.`
    )
  }
}

const mapSearchResults = (
  apiResults: SearchResponse,
  workspaceSlug: string,
  searchTerm: string
): SearchResult[] => {
  const searchResults = apiResults
    ? apiResults.data.map<SearchResult>((result) => ({
        ...result,
        id: result.id.toString(),
        isView: false,
        color: mapColor(result),
        icon: iconForEntity(result),
        useHypernovaIcon: result.type !== 'board',
      }))
    : []

  return [...makeViewResults(workspaceSlug, searchTerm), ...searchResults]
}

const mapColor = (searchResult: SearchResource): string => {
  const fallbackColor = Color.light.Text.Default

  switch (searchResult.type) {
    case 'moment':
      return searchResult.moment_color || fallbackColor
    case 'post':
      return searchResult.post_service_color || fallbackColor
    case 'story':
      return searchResult.story_color || fallbackColor
    default:
      return fallbackColor
  }
}

const makeViewResults = (
  workspaceSlug: string,
  searchTerm: string
): SearchResult[] =>
  [
    makeViewWithDefaults({
      display_name: 'Boards',
      icon: 'gem-grid',
      id: 'destination-boards',
      navigation_path: `/moments/${workspaceSlug}/boards`,
      useHypernovaIcon: false,
    }),
    makeViewWithDefaults({
      display_name: 'Calendar',
      icon: 'bs-moments',
      id: 'destination-moments',
      navigation_path: `/moments/${workspaceSlug}`,
    }),
    makeViewWithDefaults({
      display_name: 'Calendar • Day',
      icon: 'bs-moments',
      id: 'destination-calendar-day',
      navigation_path: `/moments/${workspaceSlug}#day`,
    }),
    makeViewWithDefaults({
      display_name: 'Calendar • Week',
      icon: 'bs-moments',
      id: 'destination-calendar-week',
      navigation_path: `/moments/${workspaceSlug}#week`,
    }),
    makeViewWithDefaults({
      display_name: 'Calendar • Month',
      icon: 'bs-moments',
      id: 'destination-calendar-month',
      navigation_path: `/moments/${workspaceSlug}#month`,
    }),
    makeViewWithDefaults({
      display_name: 'Stories',
      icon: 'bs-stories',
      id: 'destination-stories',
      navigation_path: `/moments/${workspaceSlug}/stories`,
    }),
    makeViewWithDefaults({
      display_name: 'Timelines',
      icon: 'bs-timelines',
      id: 'destination-timelines',
      navigation_path: `/moments/${workspaceSlug}/timelines`,
    }),
    makeViewWithDefaults({
      display_name: 'Timelines • Content',
      icon: 'bs-timelines',
      id: 'destination-timelines-content',
      navigation_path: `/moments/${workspaceSlug}/timelines#content`,
    }),
    makeViewWithDefaults({
      display_name: 'Timelines • Placements',
      icon: 'bs-timelines',
      id: 'destination-timelines-placements',
      navigation_path: `/moments/${workspaceSlug}/timelines#placements`,
    }),
    makeViewWithDefaults({
      display_name: 'Content Queue',
      icon: 'bs-content-queue',
      id: 'destination-content-queue',
      navigation_path: `/moments/${workspaceSlug}/content_queue`,
    }),
    makeViewWithDefaults({
      display_name: 'Assignments',
      icon: 'bs-assignments',
      id: 'destination-assignments',
      navigation_path: `/moments/${workspaceSlug}/assignments`,
    }),
    makeViewWithDefaults({
      display_name: 'Assignments • Content',
      icon: 'bs-assignments',
      id: 'destination-assignments-content',
      navigation_path: `/moments/${workspaceSlug}/assignments#content`,
    }),
    makeViewWithDefaults({
      display_name: 'Assignments • Moments',
      icon: 'bs-assignments',
      id: 'destination-assignments-moments',
      navigation_path: `/moments/${workspaceSlug}/assignments#moments`,
    }),
  ].filter((view) => view.display_name.toLowerCase().includes(searchTerm))

const makeViewWithDefaults = (viewResult: ViewResult): SearchResult => ({
  asset_reference_uuid: null,
  asset_reference_asset_type: null,
  asset_reference_created_at: null,
  asset_reference_full_url: null,
  asset_reference_user_name: null,
  board_created_at: null,
  board_created_by: null,
  brand_ids: [0],
  color: 'n1',
  document_version: 0,
  index_updated_at: '',
  is_private: false,
  is_visible: true,
  isView: true,
  moment_color: null,
  moment_primary_story_color: null,
  moment_primary_story_name: null,
  moment_scheduled_at: null,
  post_moment_color: null,
  post_moment_name: null,
  post_scheduled_at: null,
  post_service_color: null,
  post_service_system_name: null,
  preview_url: null,
  story_color: null,
  story_scheduled_end_at: null,
  story_scheduled_start_at: null,
  type: 'view',
  user_emails: null,
  user_job_title: null,
  useHypernovaIcon: true,
  ...viewResult,
})
